/**
 * DescendantsTab - Displays descendant (child) objectives for the currently selected objective.
 *
 * Features:
 * - "Status as of" dropdown to view status at a specific interval or "Latest"
 * - Color-coded status circles (theme-aware)
 * - Map link icons for each map a child appears on
 * - Loading and error states
 * - Empty state when no descendants exist
 *
 * Implemented in mr-ihw.3.
 */
import { useMemo } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
// mr-stiv: use drawer-local Listbox variant that renders options inline
// (no HUI portal) so clicks stay inside Map3Drawer's panelRef and do not
// trigger the capture-phase pointerdown handler's outside-click close path.
// See ./listbox-in-dialog.tsx for the full rationale.
import { Listbox, ListboxLabel, ListboxOption } from './listbox-in-dialog';
import { useDescendants } from '../../hooks/useDescendants';
import { useCurrentMap } from '../../hooks/useCurrentMap';
import { getIntervalsForMilestoneMap } from '@mm-shared/intervals';
import { formatIntervalLabel, formatShortDateString } from '@mm-shared/intervals';
import { getBackgroundColor } from '../../utils/statusUtils';
import { isValidContinuousCode } from '@mm-shared/types/statusTypeGuards';
import { getBackgroundColorClass } from '../../utils/colorUtils';
import type { ContinuousCode } from '@mm-shared/types/Status1';
import type { RootState } from '../../store/store';
import type { IntervalOption } from '@mm-shared/intervals';

/** Human-readable label for a status code */
function getStatusLabel(statusCode: string): string {
  // Handle continuous/metric codes (e.g., y12, g85, r3) by checking the leading letter
  if (isValidContinuousCode(statusCode)) {
    const letter = statusCode[0];
    if (letter === 'g') return 'Green';
    if (letter === 'y') return 'Yellow';
    if (letter === 'r') return 'Red';
  }

  switch (statusCode) {
    case 'g':
    case 'G':
      return 'Green';
    case 'y':
    case 'Y':
      return 'Yellow';
    case 'r':
    case 'R':
      return 'Red';
    case 'p':
      return 'Pending';
    case '?':
      return 'Unknown';
    default:
      return 'No status';
  }
}

const descendingByIntervalEnding = (a: { forIntervalEnding: string }, b: { forIntervalEnding: string }) =>
  new Date(b.forIntervalEnding).getTime() - new Date(a.forIntervalEnding).getTime();

export function DescendantsTab() {
  // Single searchParams hook — covers both objectiveId and dStatusAsOf params
  const [searchParams, setSearchParams] = useSearchParams();
  const objectiveShortId = searchParams.get('objectiveId') ?? undefined;

  // Get the currently selected objective to find its childIds
  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj) => obj.shortId === objectiveShortId),
  );

  const childIds = useMemo(() => objective?.childIds ?? [], [objective]);

  // Use the descendants hook for data fetching + real-time listener.
  // Pass the parent objective uuid so the listener can be keyed + cleaned up correctly.
  const { descendants, isLoading, error } = useDescendants(objective?.uuid ?? '', childIds);

  // Get the current map for intervals
  const currentMap = useCurrentMap();

  // Build interval options for the dropdown
  const intervalOptions: IntervalOption[] = useMemo(() => {
    const options: IntervalOption[] = [{ label: 'Latest', interval: null }];
    if (!currentMap) return options;
    const intervals = getIntervalsForMilestoneMap(currentMap);
    intervals.forEach((interval) => {
      options.push({
        label: formatIntervalLabel(interval, currentMap.interval),
        interval,
      });
    });
    return options;
  }, [currentMap]);

  // Persist selected interval via dStatusAsOf URL param (follows zoomLevel pattern from Map3Controls)
  const dStatusAsOf = searchParams.get('dStatusAsOf');

  const selectedOption: IntervalOption = useMemo(() => {
    if (!dStatusAsOf) return intervalOptions[0]; // "Latest" when param absent
    // Match the URL value to an interval option by comparing end-date ISO strings
    const match = intervalOptions.find(
      (opt) => opt.interval !== null && opt.interval.end.toISOString() === dStatusAsOf,
    );
    return match ?? intervalOptions[0]; // Fall back to "Latest" if no match
  }, [dStatusAsOf, intervalOptions]);

  /** Write the selected interval's end-date ISO string to the URL (or remove param for "Latest") */
  const handleIntervalChange = (option: IntervalOption) => {
    const newParams = new URLSearchParams(searchParams);
    if (option.interval === null) {
      newParams.delete('dStatusAsOf');
    } else {
      newParams.set('dStatusAsOf', option.interval.end.toISOString());
    }
    setSearchParams(newParams, { replace: true });
  };

  // --- Loading state ---
  if (isLoading) {
    return (
      <div className="py-6 text-center" role="status" aria-live="polite">
        <div className="text-sm text-gray-500">Loading descendants...</div>
      </div>
    );
  }

  // --- Error state ---
  if (error) {
    return (
      <div className="py-4" role="alert">
        <div className="text-sm text-red-600">
          Failed to load descendants: {error.message}
        </div>
      </div>
    );
  }

  // --- Empty state ---
  if (descendants.length === 0) {
    return (
      <div className="text-sm text-gray-500">
        No descendants found for this objective.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status as of dropdown */}
      <div className="flex items-center gap-2">
        <span id="status-as-of-label" className="text-sm text-gray-600 whitespace-nowrap">Status as of:</span>
        <Listbox
          value={selectedOption}
          onChange={handleIntervalChange}
          aria-label="Status as of"
        >
          {intervalOptions.map((option, idx) => (
            <ListboxOption key={idx} value={option}>
              <ListboxLabel>{option.label}</ListboxLabel>
            </ListboxOption>
          ))}
        </Listbox>
      </div>

      {/* Descendants list */}
      <ul className="space-y-3" aria-label="Descendant objectives">
        {descendants.map(({ objective: childObj, maps }) => {
          // Sort statuses once, non-mutating (fixes Redux state mutation)
          const sortedStatuses = [...childObj.statuses].sort(descendingByIntervalEnding);

          // Determine the status to display based on selected interval
          let displayStatus: typeof sortedStatuses[0] | null = null;

          if (selectedOption.interval !== null) {
            // When a specific interval is selected, find the latest *colored* (non-placeholder) status
            // within that interval. sortedStatuses is already sorted descending by forIntervalEnding,
            // so .find() returns the most recent non-placeholder status in the interval.
            //
            // NOTE: We intentionally do NOT call getLatestStatusInInterval here because that helper
            // returns any status (including 'p' placeholder codes) without filtering. Objectives on
            // weekly-interval maps produce 'p' placeholder statuses for future weeks in the current
            // month. When a monthly viewer selects 'Apr 2026', the latest-by-date entry inside April
            // is the 4/25 placeholder — not the 4/4 colored status — so the raw helper returns gray.
            // This inline filter mirrors the "Latest" mode pattern (line ~195) and fixes the regression.
            // See: mr-qtat. Out-of-scope follow-up for the shared helper: mr-t7pw.
            const { start, end } = selectedOption.interval;
            displayStatus =
              sortedStatuses.find(
                (s) =>
                  s.statusCode !== 'p' &&
                  s.statusCode !== '' &&
                  new Date(s.forIntervalEnding) > start &&
                  new Date(s.forIntervalEnding) <= end,
              ) ?? null;
          } else {
            // "Latest" mode: find the most recent g/y/r status (skip pending/empty).
            // Objectives can have placeholder statuses for future intervals with statusCode 'p'
            // that would otherwise mask colored statuses for past/current intervals.
            displayStatus =
              sortedStatuses.find((s) => s.statusCode !== 'p' && s.statusCode !== '') ?? null;
          }

          // Determine status code: use 'p' for gray when no status or pending
          const statusCode = displayStatus?.statusCode ?? 'p';
          // For color: handle continuous codes (y12, g13, r3, etc.) separately from discrete codes.
          // Continuous codes use getBackgroundColorClass; discrete codes use getBackgroundColor.
          const effectiveCode = (statusCode === 'p' || statusCode === '') ? 'p' : statusCode;
          const bgColorClass = isValidContinuousCode(effectiveCode)
            ? getBackgroundColorClass(effectiveCode as ContinuousCode)
            : getBackgroundColor(effectiveCode);
          const statusLabel = getStatusLabel(statusCode as string);

          // Most recent status date — reuse the already-sorted array
          const latestStatus = sortedStatuses.length > 0 ? sortedStatuses[0] : null;
          const statusDateStr = latestStatus?.lastUpdatedDate ?? latestStatus?.forIntervalEnding ?? null;

          return (
            <li key={childObj.uuid} className="flex items-start gap-3">
              {/* Status color circle */}
              <div className="flex-shrink-0 mt-0.5">
                <div
                  className={`h-4 w-4 rounded-full ${bgColorClass}`}
                  role="img"
                  aria-label={`Status: ${statusLabel}`}
                />
              </div>

              {/* Objective title + date annotation */}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-900">{childObj.text}</div>
                {statusDateStr ? (
                  <div className="text-xs text-gray-500 mt-0.5">
                    (status updated {formatShortDateString(statusDateStr)})
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 mt-0.5">(no status)</div>
                )}
              </div>

              {/* Map link icons — one per map the child appears on */}
              <div className="flex-shrink-0 flex items-center gap-1 mt-0.5">
                {maps.length === 0 && (
                  <span className="text-xs text-gray-500">—</span>
                )}
                {maps.map((map) => (
                  <Link
                    key={map.shortId}
                    to={`/${map.ownerName}/maps/${map.shortId}`}
                    aria-label={`Open ${childObj.text} on ${map.title || map.shortId}`}
                    className="text-gray-500 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
                  </Link>
                ))}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
